import { wccModules } from 'enums/wccModules';
import Hammer from 'hammerjs';
import heatmapjs from 'heatmap.js';
import { DOMHelpers } from 'helpers/dom';
import { ImageHelpers } from 'helpers/images';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable } from 'knockout';
import device from 'managers/device';
import { IWCCStorageManager } from 'managers/iStorage';
import { NodeTracker as Tracker } from 'managers/nodes/tracker';
import { TopicManager } from 'managers/topic';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import WCCAttachment from 'models/attachments/attachment';
import { HeatmapPoint } from 'models/heatmapPoint';
import { TopicThread } from 'models/topicThread';
import HeatmapViewType from './enums/viewType';
import './heatmap.html';
import { HeatmapConfig } from './heatmap.interfaces';
import './heatmap.scss';
import { HeatmapHintConfig } from './hint/hint';
import { HeatmapOffsetViewerConfig } from './offsetViewer/offsetViewer';
import { HeatmapViewsPanelConfig } from './viewsPanel/viewsPanel';
import { HeatmapZoomPanelConfig } from './zoomPanel/zoomPanel';

components
    .registerLocal('wcc-heatmap-hint', () => require('./hint/hint'))
    .registerLocal('wcc-heatmap-pin', () => require('./pin/pin'))
    .registerLocal('wcc-heatmap-zoom-panel', () => require('./zoomPanel/zoomPanel'))
    .registerLocal('wcc-heatmap-views-panel', () => require('./viewsPanel/viewsPanel'))
    .registerLocal('wcc-heatmap-offset-viewer', () => require('./offsetViewer/offsetViewer'))
    .preload('wcc-image');

class Pan {
    dx: number
    dy: number

    constructor(dx = 0, dy = 0) {
        this.dx = dx;
        this.dy = dy;
    }
}

class Pinch {
    scale: number

    constructor(scale = 1) {
        this.scale = scale;
    }
}

@injectable()
export default class HeatmapVM {
    private elementHeight: Subscribable<number>

    private imageOffset: Subscribable<number>
    private naturalWidth: Subscribable<number>

    private focusMode: Subscribable<boolean>
    
    private clickHandler?: Action<[number, number]>

    zoomPanel: HeatmapZoomPanelConfig
    heatmapViewsPanel: HeatmapViewsPanelConfig
    offsetViewer: HeatmapOffsetViewerConfig
    hint: HeatmapHintConfig

    imageNode = ko.observable<HTMLImageElement>();
    imageInnerNode = ko.observable<HTMLElement>();
    imageWrapperNode = ko.observable<HTMLElement>();

    dx = ko.observable(0);
    dy = ko.observable(0);
    scale = ko.observable(1);

    pan: Observable<Pan | undefined> = ko.observable();
    pinch: Observable<Pinch | undefined> = ko.observable();

    imageLoaded = ko.observable(false);

    pins: Subscribable<Array<TopicThread>>
    image: Subscribable<WCCAttachment | undefined>

    width: Subscribable<number>
    height: Subscribable<number>

    imageWidth: Subscribable<number>
    imageHeight: Subscribable<number>

    pinMaxHeight: Subscribable<number | undefined>
    imageMaxHeight: Subscribable<number | undefined>

    scaleCSS: Subscribable<string>
    translateCSS: Subscribable<string>

    ready: Subscribable<boolean>
    imageReady: Subscribable<boolean>
    loading: Subscribable<boolean>

    noBox: Subscribable<boolean>
    headerShown: Subscribable<boolean>
    allowHeatmapView: Subscribable<boolean>
    allowHint: Subscribable<boolean>
    allowZoom: Subscribable<boolean>

    isInline: Subscribable<boolean>
    isHeatmapView: Subscribable<boolean>
    isPinsView: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: HeatmapConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.clickHandler = config.click;

        const topicId = ko.flattenComputed(config.topicId);
        const isActive = ko.isWritableObservable(config.active) ? <Observable<boolean>>config.active : ko.observable(false);

        this.allowHeatmapView = ko.flattenComputed(config.heatmap, false);

        const theme = ko.flattenComputed(config.theme, 'light');
        const hint = ko.flattenComputed(config.hint, '');

        this.pins = ko.flattenComputed(config.pins, []);

        this.noBox = ko.flattenComputed(config.noBox, false);
        this.focusMode = ko.flattenComputed(config.focus, false);

        const zoomAllowed = ko.flattenComputed(config.zoom, false);
        this.allowZoom = ko.pureComputed(() => zoomAllowed() && !this.focusMode());

        this.isInline = ko.flattenComputed(config.inline, false);

        const expandedMode = ko.flattenComputed(config.expand, false);

        const topicManager = storage.get(TopicManager, { topicId });
        const topic = topicManager.pluck(m => m.topic);
        const options = topic.pluck(t => t.heatMapOptions);
        this.image = options.pluck(o => o.image);

        const { height: elementHeight } = new Tracker({ node: info.element as HTMLElement });
        this.elementHeight = elementHeight;

        const { width, height } = new Tracker({ node: this.imageWrapperNode });
        this.width = width;
        this.height = height;

        const { width: imageWidth, height: imageHeight } = new Tracker({ node: this.imageNode as Observable<HTMLElement | undefined> });
        this.imageWidth = ko.pureComputed(() => imageWidth() * this.scale());
        this.imageHeight = ko.pureComputed(() => imageHeight() * this.scale());

        const loadedImage = ko.fromPromise(img => img ? ImageHelpers.waitImage(img) : undefined, [this.imageNode]);
        this.naturalWidth = loadedImage.pluck(img => img.naturalWidth, 0);

        this.imageReady = ko.pureComputed(() => imageWidth() > 0 && imageHeight() > 0 && loadedImage() != undefined);

        this.imageOffset = ko.strictComputed((elementHeight, imageHeight) => {
            return elementHeight > 0 && imageHeight > 0 ? elementHeight - imageHeight : 0;
        }, [this.elementHeight, this.height]).extend({ rateLimit: { timeout: 0, method: 'notifyWhenChangesStop' } });

        const targetImageMaxHeight = ko.flattenComputed(config.maxHeight, 0);

        this.imageMaxHeight = ko.strictComputed((maxHeight, imageOffset) => {
            return maxHeight > 0 ? maxHeight - imageOffset : undefined;
        }, [targetImageMaxHeight, this.imageOffset]);

        this.pinMaxHeight = device.isMobile.pluck(isMobile => {
            if (isMobile)
                return undefined;

            return ko.strictComputed((height, offset) => {
                return height - offset;
            }, [this.height, this.imageOffset]);
        });

        this.scaleCSS = ko.pureComputed(() => `scale(${this.scale()})`);
        this.translateCSS = ko.pureComputed(() => `translateX(${this.dx()}px) translateY(${this.dy()}px)`);

        const waitFor = ko.flattenComputed(config.waitFor, true);
        this.ready = ko.pureComputed(() => this.imageLoaded() && waitFor());
        this.loading = this.ready.not();

        this.allowHint = ko.pureComputed(() => !this.noBox() && hint().length > 0);
        this.headerShown = ko.pureComputed(() => !this.focusMode() && (this.allowHint() || zoomAllowed() || this.allowHeatmapView()));

        const view = ko.observable(HeatmapViewType.pinnedComments);
        this.isPinsView = view.is(view => view == HeatmapViewType.pinnedComments);
        this.isHeatmapView = view.is(view => view == HeatmapViewType.heatmap);

        this.hint = { hint }
        this.zoomPanel = { heatmap: this }
        this.heatmapViewsPanel = { heatmap: this, view }

        const isOffsetViewerNeeded = ko.observable(false);
        let offsetViewerTimeout: NodeJS.Timeout | undefined;

        this.offsetViewer = {
            viewportWidth: this.width,
            viewportHeight: this.height,
            imageWidth: this.imageWidth,
            imageHeight: this.imageHeight,
            dx: this.dx,
            dy: this.dy,
            visible: isOffsetViewerNeeded
        }

        effects.register(scale => {
            if (offsetViewerTimeout != undefined) {
                clearTimeout(offsetViewerTimeout);
                offsetViewerTimeout = undefined;
            }

            if (scale > 1)
                isOffsetViewerNeeded(true);

            if (isOffsetViewerNeeded())
                offsetViewerTimeout = setTimeout(() => isOffsetViewerNeeded(false), 2000);
        }, [this.scale, this.dx, this.dy]);

        effects.register((imageReady, isHeatmapView, node) => {
            if (imageReady && isHeatmapView && node != undefined) {
                const heatmap = heatmapjs.create({
                    minOpacity: 0,
                    maxOpacity: 0.6,
                    container: node
                });


                return [
                    () => $(node).find('.heatmap-canvas').remove(),

                    withEffect((pins, imageWidth, imageHeight) => {
                        heatmap.setData({
                            min: 0,
                            max: 1,
                            data: pins
                                .map(pin => pin.point())
                                .filter<HeatmapPoint>(point => point != undefined)
                                .map(point => ({
                                    x: Math.ceil(imageWidth * point.x()),
                                    y: Math.ceil(imageHeight * point.y()),
                                    value: 1
                                }))
                        });
                    }, [this.pins, this.imageWidth, this.imageHeight])
                ]
            }
        }, [this.imageReady, this.isHeatmapView, this.imageInnerNode]);

        effects.register(() => {
            return [
                device.onResize(() => this.move(0, 0)),
                () => isActive(false)
            ]
        });

        effects.register(async isReady => {
            isActive(isReady);

            if (isReady) {
                this.scale(1);

                await this.imageReady.when();

                const imageHeightValue = await imageHeight.when(v => v > 0);
                const heightValue = await this.height.when(v => v > 0);

                this.move(0, imageHeightValue > heightValue ? Number.POSITIVE_INFINITY : 0);
            }
        }, [this.ready, this.image]);

        effects.register((node, allowZoom) => {
            if (allowZoom && node != undefined) {
                const hammer = new Hammer(node, {
                    recognizers: [
                        [Hammer.Tap],
                        [Hammer.Pan, { threshold: 0 }],
                        [Hammer.Pinch, { enable: true }]
                    ]
                });

                hammer.on('tap', this.onTap.bind(this));
                hammer.on('panstart', this.onPanStart.bind(this));
                hammer.on('panmove', this.onPanMove.bind(this));
                hammer.on('panend', this.onPanEnd.bind(this));
                hammer.on('pinchstart', this.onPinchStart.bind(this));
                hammer.on('pinch', this.onPinch.bind(this));
                hammer.on('pinchend', this.onPinchEnd.bind(this));

                return withEffect(() => [
                    DOMHelpers.onNodeEvent(node, 'wheel', e => this.onWheel(e.originalEvent as WheelEvent)),
                    () => hammer.destroy()
                ]);
            }
        }, [this.imageNode, this.allowZoom]);

        effects.register([
            ko.computed(() => this.handleFocus())
        ]);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'wcc-heatmap': true,
                'wcc-heatmap--inline': this.isInline,
                'wcc-heatmap--ready': this.ready,
                'wcc-heatmap--expanded': expandedMode,
                'wcc-heatmap--mobile': device.isMobile,
                'wcc-heatmap--no-hint': hint.isEmpty()
            }
        });

        ko.applyBindingsToNode(info.element, {
            'css': theme.pluck(theme => `wcc-heatmap--${theme}-theme`)
        });
    }

    zoomIn(delta = 0.15) {
        this.scaleAtCenter(1 + delta);
    }

    zoomOut(delta = 0.15) {
        this.scaleAtCenter(1 - delta);
        this.move(0, 0);
    }

    private move(dx: number, dy: number, force = false) {
        if (!this.allowZoom() && !force) return;

        const width = this.width();
        const height = this.height();

        const imageWidth = this.imageWidth();
        const imageHeight = this.imageHeight();

        //lets calculate max offsets as difference between image and container sizes
        //but we scale image so offsets will be scaled too
        const maxDx = imageWidth > width ? (imageWidth - width) / 2 : 0;
        const maxDy = imageHeight > height ? (imageHeight - height) / 2 : 0;

        const newDx = this.dx() + dx;
        const newDy = this.dy() + dy;

        if (Math.abs(newDx) < maxDx) {
            this.dx(newDx);
        } else {
            this.dx(newDx < 0 ? -maxDx : maxDx);
        }

        if (Math.abs(newDy) < maxDy) {
            this.dy(newDy);
        } else {
            this.dy(newDy < 0 ? -maxDy : maxDy);
        }
    }

    private scaleAt(absX: number, absY: number, delta: number) {
        if (!this.allowZoom()) return;

        const { x, y } = this.getRelCoords(absX, absY);

        const scale = this.scale();
        const width = this.imageWidth() / scale;
        const height = this.imageHeight() / scale;

        const newScale = this.scaleAtCenter(delta);
        const realDelta = newScale - scale;

        const dx = (0.5 - x) * width * realDelta;
        const dy = (0.5 - y) * height * realDelta;

        this.move(dx, dy);
    }

    private scaleAtCenter(delta: number) {
        const scale = this.scale();

        const maxScale = this.naturalWidth() / this.width() * 2;
        const minScale = 1;

        let newScale = scale * delta;

        if (newScale > maxScale)
            newScale = maxScale;

        if (newScale < minScale)
            newScale = minScale;

        this.scale(newScale);

        return newScale;
    }

    private getRelCoords(absX: number, absY: number) {
        return this.imageWrapperNode.invokeNotNull(wrapper => {
            const { width, height, top, left } = wrapper.getBoundingClientRect();

            const imageWidth = this.imageWidth();
            const imageHeight = this.imageHeight();

            const centerX = width / 2;
            const centerY = height / 2;

            const imageCenterX = imageWidth / 2;
            const imageCenterY = imageHeight / 2;

            //top-left corner of wrapper
            const baseX = imageCenterX - this.dx() - centerX;
            const baseY = imageCenterY - this.dy() - centerY;

            if (width > 0 && height > 0) {
                //coordinates relative to wrapper's top-left corner
                const wrapperX = absX - left;
                const wrapperY = absY - top;

                let x = (wrapperX + baseX) / imageWidth,
                    y = (wrapperY + baseY) / imageHeight;

                if (x < 0) x = 0;
                if (x > 1) x = 1;
                if (y < 0) y = 0;
                if (y > 1) y = 1;

                return { x, y };
            }
        }) ?? { x: 0, y: 0 };
    }

    private handleFocus() {
        if (!this.focusMode()) return;

        var pin = _(this.pins()).first();

        if (pin !== undefined) {
            var point = pin.point();

            if (point != null) {
                var x = point.x(),
                    y = point.y();

                if (x != null && y != null && this.width() > 0) {
                    var scale = this.naturalWidth() / this.width();

                    var dx = (0.5 - x) * this.width() * scale,
                        dy = (0.5 - y) * this.height() * scale;

                    this.scale(scale);
                    this.dx(0);
                    this.dy(0);

                    this.move(dx, dy, true);
                }
            }
        }
    }

    private onTap(e: HammerInput) {
        const coords = this.getRelCoords(e.center.x, e.center.y);

        this.clickHandler?.(coords.x, coords.y);
    }

    private onPanStart(e: HammerInput) {
        e.preventDefault();
        this.pan(new Pan());
    }

    private onPanMove(e: HammerInput) {
        e.preventDefault();

        var pan = this.pan() || new Pan(),
            dx = e.deltaX - pan.dx,
            dy = e.deltaY - pan.dy;

        this.move(dx, dy);
        this.pan(new Pan(e.deltaX, e.deltaY));
    }

    private onPanEnd(e: HammerInput) {
        e.preventDefault();
        this.pan(undefined);
    }

    private onPinchStart(e: HammerInput) {
        e.preventDefault();
        this.pinch(new Pinch());
    }

    private onPinch(e: HammerInput) {
        e.preventDefault();

        var pinch = this.pinch() || new Pinch(),
            scale = e.scale / pinch.scale;

        this.scaleAt(e.center.x, e.center.y, scale);
        this.pinch(new Pinch(e.scale));
    }

    private onPinchEnd(e: HammerInput) {
        e.preventDefault();
        this.pinch(undefined);
    }

    private onWheel(e: WheelEvent) {
        if (e.ctrlKey) {
            e.preventDefault();

            this.scaleAt(e.clientX, e.clientY, 1 - e.deltaY * 0.001);
        }
    }
}